import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from 'src/components/Dialog/Dialog';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import { getClientById } from 'src/services/clientService';
import { getBasicContractList } from 'src/services/contractService';
import {
  loadAllClients,
  LOAD_CLIENTS
} from 'src/store/actions/clientActions';
import {
  editSurchargeExclusionsParameters,
  EDIT_SURCHARGE_EXCLUSIONS_PARAMETER
} from 'src/store/actions/parameterActions';
import { selectClientsList } from 'src/store/selectors/clientSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { showMessage } from 'src/store/actions/snackBarActions';
import { SnackBarVariants } from 'src/utils/constants/common';
import {
  transformJsonToArray,
  transformArrayToString
} from 'src/utils/common';

const TabCountBadge = ({ count, active }) => (
  <Box
    component="span"
    sx={theme => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '20px',
      height: '20px',
      padding: '0 6px',
      marginLeft: '8px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: active
        ? theme.palette.badge.info.bg
        : theme.palette.badge.neutral.bg,
      color: active
        ? theme.palette.badge.info.text
        : theme.palette.badge.neutral.text
    })}
  >
    {count}
  </Box>
);

TabCountBadge.propTypes = {
  count: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired
};

const ExclusionList = ({ emptyLabel, items, loading, onRemove }) => {
  if (loading) return <CircularProgress size={24} />;

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Box
      sx={theme => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px'
      })}
    >
      <List disablePadding>
        {items.map((item, index) => (
          <ListItem
            key={item.id}
            divider={index < items.length - 1}
            sx={{ py: 1.5, px: 2 }}
          >
            <ListItemText primary={item.label} />
            <ListItemSecondaryAction>
              <IconButton edge="end" onClick={() => onRemove(item)} color="error">
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

ExclusionList.propTypes = {
  emptyLabel: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onRemove: PropTypes.func.isRequired
};

const CLIENTS_TAB = 0;
const CONTRACTS_TAB = 1;

const SurchargeExclusionsModal = ({
  open,
  handleClose,
  clientsParameter,
  contractsParameter
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(CLIENTS_TAB);

  const allClients = useSelector(selectClientsList);
  const isLoadingClients = useSelector(
    isLoadingSelector([LOAD_CLIENTS.action])
  );
  const [allContracts, setAllContracts] = useState([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const loadingSave = useSelector(
    isLoadingSelector([EDIT_SURCHARGE_EXCLUSIONS_PARAMETER.action])
  );

  const [excludedClients, setExcludedClients] = useState([]);
  const [loadingExcludedClients, setLoadingExcludedClients] = useState(false);

  const [excludedContracts, setExcludedContracts] = useState([]);
  const [loadingExcludedContracts, setLoadingExcludedContracts] = useState(
    false
  );

  useEffect(() => {
    if (!open) {
      setActiveTab(CLIENTS_TAB);
      setExcludedClients([]);
      setExcludedContracts([]);
      setAllContracts([]);
      return;
    }

    dispatch(loadAllClients());

    const clientIds = transformJsonToArray(
      clientsParameter && clientsParameter.value
    ).filter(Boolean);

    if (clientIds.length > 0) {
      setLoadingExcludedClients(true);
      Promise.all(
        clientIds.map(id =>
          getClientById(id)
            .then(c => ({ id: String(id), label: c.fullName || String(id) }))
            .catch(() => ({ id: String(id), label: String(id) }))
        )
      )
        .then(setExcludedClients)
        .finally(() => setLoadingExcludedClients(false));
    } else {
      setExcludedClients([]);
    }

    const contractIds = transformJsonToArray(
      contractsParameter && contractsParameter.value
    ).filter(Boolean);

    setIsLoadingContracts(true);
    setLoadingExcludedContracts(contractIds.length > 0);
    getBasicContractList()
      .then(contracts => {
        setAllContracts(contracts);

        if (contractIds.length === 0) {
          setExcludedContracts([]);
          return;
        }

        const contractsById = new Map(
          contracts.map(c => [String(c.id), c])
        );
        setExcludedContracts(
          contractIds.map(id => {
            const found = contractsById.get(String(id));
            return found
              ? { id: String(id), label: `${found.folio} — ${found.clientName || ''}` }
              : { id: String(id), label: `Contrato #${id} (no activo)` };
          })
        );
      })
      .catch(error => {
        setAllContracts([]);
        setExcludedContracts(
          contractIds.map(id => ({
            id: String(id),
            label: `Contrato #${id}`
          }))
        );
        dispatch(
          showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
        );
      })
      .finally(() => {
        setIsLoadingContracts(false);
        setLoadingExcludedContracts(false);
      });
  }, [open, clientsParameter, contractsParameter]);

  const clientOptions = useMemo(() => {
    const excludedIds = new Set(excludedClients.map(c => c.id));
    return (allClients || [])
      .filter(c => !excludedIds.has(String(c.id)))
      .map(c => ({
        id: String(c.id),
        label: `${c.id} - ${c.fullName || ''}`
      }));
  }, [allClients, excludedClients]);

  const handleAddClient = useCallback(client => {
    if (!client) return;
    setExcludedClients(prev => [{ id: client.id, label: client.label }, ...prev]);
  }, []);

  const handleRemoveClient = useCallback(client => {
    setExcludedClients(prev => prev.filter(c => c.id !== client.id));
  }, []);

  const contractOptions = useMemo(() => {
    const excludedIds = new Set(excludedContracts.map(c => c.id));
    return (allContracts || [])
      .filter(c => !excludedIds.has(String(c.id)))
      .map(c => ({
        id: String(c.id),
        label: `${c.folio} — ${c.clientName || ''}`
      }));
  }, [allContracts, excludedContracts]);

  const handleAddContract = useCallback(contract => {
    if (!contract) return;
    setExcludedContracts(prev => {
      if (prev.some(c => c.id === contract.id)) return prev;
      return [{ id: contract.id, label: contract.label }, ...prev];
    });
  }, []);

  const handleRemoveContract = useCallback(contract => {
    setExcludedContracts(prev => prev.filter(c => c.id !== contract.id));
  }, []);

  const handleSave = useCallback(() => {
    const clientIds = transformArrayToString(excludedClients.map(c => c.id));
    const contractIds = transformArrayToString(
      excludedContracts.map(c => c.id)
    );

    dispatch(
      editSurchargeExclusionsParameters(
        clientsParameter,
        clientIds,
        contractsParameter,
        contractIds,
        handleClose
      )
    );
  }, [
    excludedClients,
    excludedContracts,
    clientsParameter,
    contractsParameter,
    dispatch,
    handleClose
  ]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={t('PARAMETER_VIEW.SURCHARGE_EXCLUSIONS_TITLE')}
      maxWidth="md"
      fullWidth
      sx={{ '& .MuiDialog-paper': { width: '700px', maxWidth: '90vw' } }}
    >
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {t('PARAMETER_VIEW.SURCHARGE_EXCLUSIONS_DESCRIPTION')}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, val) => setActiveTab(val)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center">
                {t('COMMON.CLIENTS')}
                <TabCountBadge
                  count={excludedClients.length}
                  active={activeTab === CLIENTS_TAB}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                {t('COMMON.CONTRACTS')}
                <TabCountBadge
                  count={excludedContracts.length}
                  active={activeTab === CONTRACTS_TAB}
                />
              </Box>
            }
          />
        </Tabs>

        {activeTab === CLIENTS_TAB && (
          <Box mb={3} sx={{ width: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('PARAMETER_VIEW.SURCHARGE_EXCLUDED_CLIENTS')}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              {t('PARAMETER_VIEW.SURCHARGE_CLIENTS_TAB_DESCRIPTION')}
            </Typography>
            <Autocomplete
              options={clientOptions}
              value={null}
              getOptionLabel={option => option.label || ''}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              isLoading={isLoadingClients}
              noOptionsText={t('COMMON.NO_DATA')}
              label={t('COMMON.CLIENT')}
              onChange={(_e, val) => handleAddClient(val)}
              sx={{ marginBottom: 2 }}
            />
            <ExclusionList
              emptyLabel={t('PARAMETERS_VIEW.NO_SURCHARGE_EXCLUDED_ITEMS')}
              items={excludedClients}
              loading={loadingExcludedClients}
              onRemove={handleRemoveClient}
            />
          </Box>
        )}

        {activeTab === CONTRACTS_TAB && (
          <Box mb={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('PARAMETER_VIEW.SURCHARGE_EXCLUDED_CONTRACTS')}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              {t('PARAMETER_VIEW.SURCHARGE_CONTRACTS_TAB_DESCRIPTION')}
            </Typography>
            <Autocomplete
              options={contractOptions}
              value={null}
              getOptionLabel={option => option.label || ''}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              isLoading={isLoadingContracts}
              noOptionsText={t('COMMON.NO_DATA')}
              label={t('COMMON.CONTRACT')}
              onChange={(_e, val) => handleAddContract(val)}
              sx={{ marginBottom: 2 }}
            />
            <ExclusionList
              emptyLabel={t('PARAMETERS_VIEW.NO_SURCHARGE_EXCLUDED_ITEMS')}
              items={excludedContracts}
              loading={loadingExcludedContracts}
              onRemove={handleRemoveContract}
            />
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ width: '100%' }}
            disabled={loadingSave}
          >
            {t('COMMON.CANCEL')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ width: '100%' }}
            disabled={
              loadingSave || loadingExcludedClients || loadingExcludedContracts
            }
            onClick={handleSave}
          >
            {loadingSave ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('COMMON.SAVE')
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

SurchargeExclusionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  clientsParameter: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    value: PropTypes.string,
    type: PropTypes.string
  }),
  contractsParameter: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    value: PropTypes.string,
    type: PropTypes.string
  })
};

export default SurchargeExclusionsModal;
